import {
  faCheckCircle,
  faCreditCard,
  faEdit,
  faPlus,
  faReceipt,
  faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Card } from 'src/components/Card';
import { Chip } from 'src/components/Chip';
import {
  ConfirmDialog,
  useConfirmDialogComponent,
} from 'src/components/ConfirmDialog';
import { Icon } from 'src/components/Icon';
import { SearchParamsPagination } from 'src/components/Pagination';
import { SearchParamSearchTextField } from 'src/components/SearchTextField';
import { SectionToolbar } from 'src/components/SectionToolbar';
import { Stack } from 'src/components/Stack';
import { DataTable } from 'src/components/Table/DataTable';
import { Typography } from 'src/components/Typography';
import { AppFrameConfig } from 'src/shared/AppFrame/AppFrameConfig';
import { ContainerBreakout } from 'src/shared/AppFrame/ContainerBreakout';
import { useHQPermissionCheck } from 'src/shared/HQ/useHQPermissionCheck';
import toast from 'src/utils/toast';
import { useListSearchParams } from 'src/utils/useListSearchParams';

import {
  FranchiseFeePayer,
  FranchiseFeePayersQueryKey,
  deleteFranchiseFeePayer,
  fetchFranchiseFeePayers,
} from '../../../models/hq/franchise-fee-payer/franchise-fee-payer';

import PaymentHistoryDialog from './PaymentHistoryDialog';
import PaymentMethodDialog from './PaymentMethodDialog';

const FranchiseFeePayersPage: React.FC = () => {
  const { canEditHQFranchiseFees } = useHQPermissionCheck();
  const { t } = useTranslation('translation', {
    keyPrefix: 'franchiseFeePayers',
  });
  const navigate = useNavigate();

  const { params, updateParam } = useListSearchParams({
    defaultPerPage: 20,
    paramPrefix: 'payer_',
  });

  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: [FranchiseFeePayersQueryKey, params],
    queryFn: () =>
      fetchFranchiseFeePayers({
        page: params.page,
        perPage: params.perPage,
        search: params.search,
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteFranchiseFeePayer,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FranchiseFeePayersQueryKey] });
      toast.success(t('payerDeleted'));
    },
    onError: () => {
      toast.error(t('errorDeleting'));
    },
  });

  const [confirmDeleteProps, confirmDelete] = useConfirmDialogComponent({
    title: t('confirmDelete'),
    message: t('confirmDeleteMessage'),
    confirmText: t('delete'),
    cancelText: t('cancel'),
  });

  // Payment method dialog state
  const [paymentMethodDialogOpen, setPaymentMethodDialogOpen] = useState(false);
  const [selectedPayerForPayment, setSelectedPayerForPayment] =
    useState<FranchiseFeePayer | null>(null);

  // Payment history dialog state
  const [paymentHistoryDialogOpen, setPaymentHistoryDialogOpen] =
    useState(false);
  const [selectedPayerForHistory, setSelectedPayerForHistory] =
    useState<FranchiseFeePayer | null>(null);

  const handleEdit = useCallback(
    (id: FranchiseFeePayer['id']) => {
      navigate(`/franchise-fees/payers/${id}`);
    },
    [navigate]
  );

  const handleDelete = useCallback(
    async (payer: FranchiseFeePayer) => {
      if (!payer.canBeDeleted) {
        toast.error(t('cannotDeleteWithPayouts'));
        return;
      }

      if (await confirmDelete()) {
        deleteMutation.mutate(payer.id);
      }
    },
    [confirmDelete, deleteMutation, t]
  );

  const handleManagePaymentMethod = useCallback((payer: FranchiseFeePayer) => {
    setSelectedPayerForPayment(payer);
    setPaymentMethodDialogOpen(true);
  }, []);

  const handlePaymentMethodAdded = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [FranchiseFeePayersQueryKey] });
  }, [queryClient]);

  const handleClosePaymentMethodDialog = useCallback(() => {
    setPaymentMethodDialogOpen(false);
    setSelectedPayerForPayment(null);
  }, []);

  const handleViewPaymentHistory = useCallback((payer: FranchiseFeePayer) => {
    setSelectedPayerForHistory(payer);
    setPaymentHistoryDialogOpen(true);
  }, []);

  const handleClosePaymentHistoryDialog = useCallback(() => {
    setPaymentHistoryDialogOpen(false);
    setSelectedPayerForHistory(null);
  }, []);

  const columns = useMemo(
    () => [
      {
        id: 'name',
        label: t('name'),
        sortable: true,
        format: (value: string) => <Typography>{value}</Typography>,
      },
      {
        id: 'email',
        label: t('email'),
        sortable: true,
      },
      {
        id: 'platformNames',
        label: t('platforms'),
        format: (value: []) =>
          value.map((v) => (
            <Chip label={v} size="small" variant="outlined" key={v} />
          )),
      },
      {
        id: 'paymentMethod',
        label: t('paymentMethod'),
        format: (value: boolean, row: FranchiseFeePayer) =>
          value ? (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Icon icon={faCheckCircle} color="success" size="small" />
              <Typography variant="body2">
                {row.paymentMethodMasked || t('connected')}
              </Typography>
            </Stack>
          ) : (
            <Button
              variant="bordered"
              size="small"
              startIcon={<Icon icon={faPlus} />}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleManagePaymentMethod(row);
              }}
            >
              {t('addPaymentMethod')}
            </Button>
          ),
      },
    ],
    [t, handleManagePaymentMethod]
  );

  const rowActions = useMemo(
    () => [
      ...(canEditHQFranchiseFees()
        ? [
            {
              icon: faEdit,
              label: t('edit'),
              onClick: (row: FranchiseFeePayer) => handleEdit(row.id),
            },
          ]
        : []),
      {
        icon: faReceipt,
        label: t('viewPaymentHistory'),
        onClick: (row: FranchiseFeePayer) => handleViewPaymentHistory(row),
      },
      {
        icon: faCreditCard,
        label: t('managePaymentMethod'),
        onClick: (row: FranchiseFeePayer) => handleManagePaymentMethod(row),
      },
      ...(canEditHQFranchiseFees()
        ? [
            {
              icon: faTrash,
              label: t('delete'),
              onClick: (row: FranchiseFeePayer) => handleDelete(row),
              showAction: (row: FranchiseFeePayer) => row.canBeDeleted,
            },
          ]
        : []),
    ],
    [
      canEditHQFranchiseFees,
      t,
      handleEdit,
      handleViewPaymentHistory,
      handleManagePaymentMethod,
      handleDelete,
    ]
  );

  const rows = data?.franchiseFeePayers || [];
  const total = data?.total || 0;

  return (
    <>
      <AppFrameConfig
        id="franchise-fee-payers"
        containerMaxWidth={'xl'}
        title={t('franchiseFeePayers')}
        actions={
          canEditHQFranchiseFees() ? (
            <Button
              variant="prominent"
              startIcon={<Icon icon={faPlus} />}
              onClick={() => navigate('/franchise-fees/payers/new')}
            >
              {t('addPayer')}
            </Button>
          ) : undefined
        }
      />
      <ContainerBreakout>
        <Box>
          <SectionToolbar sx={{ pb: 3, pt: 0, px: 0 }}>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ width: '100%' }}
            >
              <SearchParamSearchTextField
                params={params}
                updateParam={updateParam}
                placeholder={t('searchPayers')}
                sx={{ width: '280px' }}
              />
            </Stack>
          </SectionToolbar>
          <Card>
            <DataTable
              columns={columns}
              rows={rows}
              rowActions={rowActions}
              loading={isLoading}
              noResultsText={t('noPayersFound')}
              totalItems={total}
              itemsPerPage={params.perPage}
              actionTitle={t('actions')}
              rowLinkTo={
                canEditHQFranchiseFees()
                  ? (row) => `/franchise-fees/payers/${row.id}`
                  : undefined
              }
            />

            {total > 0 && (
              <SearchParamsPagination
                params={params}
                updateParam={updateParam}
                total={total}
                itemsPerPage={params.perPage}
              />
            )}
          </Card>

          <ConfirmDialog {...confirmDeleteProps} />

          <PaymentMethodDialog
            open={paymentMethodDialogOpen}
            onClose={handleClosePaymentMethodDialog}
            payer={selectedPayerForPayment}
            onPaymentMethodAdded={handlePaymentMethodAdded}
          />

          <PaymentHistoryDialog
            open={paymentHistoryDialogOpen}
            onClose={handleClosePaymentHistoryDialog}
            payer={selectedPayerForHistory}
          />
        </Box>
      </ContainerBreakout>
    </>
  );
};

export default FranchiseFeePayersPage;
